import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES, SCROLL } from "../../data/content";

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

const Fade = ({ children, delay, className = "" }) => (
    <motion.div
        className={className}
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
        {children}
    </motion.div>
);

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

    return (
        <section ref={ref} className="relative h-[100svh] min-h-[600px] overflow-hidden bg-navy" data-testid="hero-section">
            <motion.div style={{ y: bgY }} className="absolute inset-0">
                <motion.img
                    src={IMAGES.hero.src}
                    alt={IMAGES.hero.alt}
                    initial={{ scale: 1.15, opacity: 0.6 }}
                    animate={{ scale: 1.06, opacity: 1 }}
                    transition={{ duration: 2.6, ease: [0.22, 1, 0.36, 1] }}
                    className="h-full w-full object-cover"
                    fetchPriority="high"
                />
                <div className="absolute inset-0 bg-navy/60" />
                <div className="absolute inset-0 bg-gradient-to-b from-navy/50 via-transparent to-navy/70" />
            </motion.div>

            <div className="relative z-10 h-full container-luxe flex flex-col justify-end pb-24 sm:pb-28">
                <Fade delay={0.2}>
                    <p className="eyebrow mb-6">Travel Your Way · Experience Like a Local</p>
                </Fade>

                <h1 className="font-serif text-cream text-5xl sm:text-6xl lg:text-7xl leading-[1.08] tracking-tight">
                    <MaskedLine delay={0.35}>Journeys,</MaskedLine>
                    <MaskedLine delay={0.5}>
                        <span className="italic text-gold">Unscripted.</span>
                    </MaskedLine>
                </h1>

                <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 1.1, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="origin-left h-px w-24 bg-gold mt-8"
                />

                <Fade delay={1.05} className="mt-6 max-w-xl">
                    <p className="text-cream/85 font-light leading-relaxed text-base sm:text-lg">
                        Private and small-group travel, designed around how you like to travel.
                    </p>
                </Fade>

                <Fade delay={1.2} className="mt-9 flex flex-col sm:flex-row gap-4">
                    <button data-testid="hero-cta-plan" onClick={() => SCROLL.go("contact")} className="btn-gold">
                        Plan My Journey
                    </button>
                    <button data-testid="hero-cta-packages" onClick={() => SCROLL.go("packages")} className="btn-ghost-light">
                        Explore Packages
                    </button>
                </Fade>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8, duration: 1 }}
                className="absolute bottom-8 right-8 z-10 hidden sm:flex flex-col items-center gap-3"
                aria-hidden="true"
            >
                <span className="text-[10px] tracking-[0.3em] uppercase text-cream/50 [writing-mode:vertical-rl]">Scroll</span>
                <span className="h-14 w-px bg-gradient-to-b from-gold to-transparent" />
            </motion.div>
        </section>
    );
}
